import type { PalaceId } from './domain.js';
import type { InterpretationRulePack, TemplatePack } from './interpretation.js';
import type { RuleCondition, TopicId } from './rules.js';
import type { RuleCoveragePolicy } from './ruleCoveragePolicy.js';
import { STAR_REGISTRY } from './starRegistry.js';

const PALACE_IDS: readonly PalaceId[] = Object.freeze([
  'life','siblings','spouse','children','wealth','health',
  'travel','friends','career','property','wellbeing','parents',
]);
const MAJOR_STAR_IDS: readonly string[] = Object.freeze(STAR_REGISTRY.filter((star) => star.family === 'major').map((star) => star.id));
type RulePolarity = 'support' | 'counter' | 'neutral';
type ConditionKind = 'featureExists' | 'compare' | 'compareFeatures' | 'all' | 'any' | 'not';

export interface RulePackQaReport {
  ruleCount: number;
  claimKeyCount: number;
  topicRuleCounts: Readonly<Partial<Record<TopicId, number>>>;
  sourceRuleCounts: Readonly<Record<string, number>>;
  sourceLocatorRuleCounts: Readonly<Record<string, number>>;
  polarityRuleCounts: Readonly<Record<RulePolarity, number>>;
  conditionRuleCounts: Readonly<Record<ConditionKind, number>>;
  referencedPalaceRuleCounts: Readonly<Partial<Record<PalaceId, number>>>;
  referencedStarRuleCounts: Readonly<Record<string, number>>;
  unreferencedPalaceIds: readonly PalaceId[];
  unreferencedMajorStarIds: readonly string[];
  heldPalaceIds: readonly PalaceId[];
  heldMajorStarIds: readonly string[];
  actionableUnreferencedPalaceIds: readonly PalaceId[];
  actionableUnreferencedMajorStarIds: readonly string[];
  coverageHoldReasons: Readonly<Record<string, string>>;
  dimensionPolarityRuleCounts: Readonly<Record<string, Readonly<Record<RulePolarity, number>>>>;
  dimensionsWithoutCounterRules: readonly string[];
  dimensionsWithoutSupportRules: readonly string[];
  singleFeatureRuleCount: number;
  multiFeatureRuleCount: number;
  claimKeysWithoutTemplates: readonly string[];
  templatesWithoutRules: readonly string[];
  incompleteProvenanceRuleIds: readonly string[];
  disclosure: string;
}

function increment(map: Record<string, number>, key: string): void {
  map[key] = (map[key] ?? 0) + 1;
}

function conditionFeatureIds(condition: RuleCondition, output = new Set<string>()): Set<string> {
  if ('all' in condition) condition.all.forEach((item) => conditionFeatureIds(item, output));
  else if ('any' in condition) condition.any.forEach((item) => conditionFeatureIds(item, output));
  else if ('not' in condition) conditionFeatureIds(condition.not, output);
  else if ('featureExists' in condition) output.add(condition.featureExists);
  else if ('compare' in condition) output.add(condition.compare.featureId);
  else if ('compareFeatures' in condition) {
    output.add(condition.compareFeatures.leftFeatureId);
    output.add(condition.compareFeatures.rightFeatureId);
  }
  return output;
}

function conditionKinds(condition: RuleCondition, output = new Set<ConditionKind>()): Set<ConditionKind> {
  if ('all' in condition) { output.add('all'); condition.all.forEach((item) => conditionKinds(item, output)); }
  else if ('any' in condition) { output.add('any'); condition.any.forEach((item) => conditionKinds(item, output)); }
  else if ('not' in condition) { output.add('not'); conditionKinds(condition.not, output); }
  else if ('featureExists' in condition) output.add('featureExists');
  else if ('compare' in condition) output.add('compare');
  else if ('compareFeatures' in condition) output.add('compareFeatures');
  return output;
}

function palaceRefs(featureIds: ReadonlySet<string>): Set<PalaceId> {
  const refs = new Set<PalaceId>();
  for (const id of featureIds) {
    const tokens = id.split('.');
    PALACE_IDS.forEach((palaceId) => { if (tokens.includes(palaceId)) refs.add(palaceId); });
  }
  return refs;
}

function starRefs(featureIds: ReadonlySet<string>): Set<string> {
  const refs = new Set<string>();
  for (const id of featureIds) {
    const tokens = id.split('.');
    STAR_REGISTRY.forEach((star) => { if (tokens.includes(star.id)) refs.add(star.id); });
  }
  return refs;
}

export function buildRulePackQaReport(rulePack: InterpretationRulePack, templatePack: TemplatePack, coveragePolicy?: RuleCoveragePolicy): RulePackQaReport {
  const topicRuleCounts: Partial<Record<TopicId, number>> = {};
  const sourceRuleCounts: Record<string, number> = {};
  const sourceLocatorRuleCounts: Record<string, number> = {};
  const polarityRuleCounts: Record<RulePolarity, number> = { support:0, counter:0, neutral:0 };
  const conditionRuleCounts: Record<ConditionKind, number> = { featureExists:0, compare:0, compareFeatures:0, all:0, any:0, not:0 };
  const referencedPalaceRuleCounts: Partial<Record<PalaceId, number>> = {};
  const referencedStarRuleCounts: Record<string, number> = {};
  const dimensionPolarityRuleCounts: Record<string, Record<RulePolarity, number>> = {};
  const claimKeys = new Set<string>();
  const incompleteProvenanceRuleIds: string[] = [];
  let singleFeatureRuleCount = 0;
  let multiFeatureRuleCount = 0;

  for (const rule of rulePack.rules) {
    topicRuleCounts[rule.topic] = (topicRuleCounts[rule.topic] ?? 0) + 1;
    increment(sourceRuleCounts, rule.source.sourceId);
    if (rule.sourceLocator) increment(sourceLocatorRuleCounts, rule.sourceLocator);
    polarityRuleCounts[rule.polarity] += 1;
    const featureIds = conditionFeatureIds(rule.conditions);
    const kinds = conditionKinds(rule.conditions);
    kinds.forEach((kind) => { conditionRuleCounts[kind] += 1; });
    palaceRefs(featureIds).forEach((palaceId) => {
      referencedPalaceRuleCounts[palaceId] = (referencedPalaceRuleCounts[palaceId] ?? 0) + 1;
    });
    starRefs(featureIds).forEach((starId) => increment(referencedStarRuleCounts, starId));
    if (featureIds.size === 1) singleFeatureRuleCount += 1;
    if (featureIds.size >= 2) multiFeatureRuleCount += 1;
    for (const dimension of new Set(rule.effects.map((effect) => effect.dimension))) {
      const counts = dimensionPolarityRuleCounts[dimension] ?? { support:0, counter:0, neutral:0 };
      counts[rule.polarity] += 1;
      dimensionPolarityRuleCounts[dimension] = counts;
    }
    if (rule.source.sourceType !== 'test_fixture' && (!rule.sourceLocator?.trim() || !rule.normalizedRationale?.trim())) {
      incompleteProvenanceRuleIds.push(rule.id);
    }
    rule.effects.forEach((effect) => claimKeys.add(effect.claimKey));
  }

  const templateKeys = Object.keys(templatePack.templates);
  const claimKeysWithoutTemplates = [...claimKeys].filter((key) => !templatePack.templates[key]).sort();
  const templatesWithoutRules = templateKeys.filter((key) => !claimKeys.has(key)).sort();
  const unreferencedPalaceIds = PALACE_IDS.filter((id) => !referencedPalaceRuleCounts[id]);
  const unreferencedMajorStarIds = MAJOR_STAR_IDS.filter((id) => !referencedStarRuleCounts[id]);
  const heldPalaceIds = (coveragePolicy?.palaceHolds ?? []).map((item) => item.id).filter((id) => unreferencedPalaceIds.includes(id));
  const heldMajorStarIds = (coveragePolicy?.majorStarHolds ?? []).map((item) => item.id).filter((id) => unreferencedMajorStarIds.includes(id));
  const heldPalaceSet = new Set(heldPalaceIds);
  const heldMajorStarSet = new Set(heldMajorStarIds);
  const actionableUnreferencedPalaceIds = unreferencedPalaceIds.filter((id) => !heldPalaceSet.has(id));
  const actionableUnreferencedMajorStarIds = unreferencedMajorStarIds.filter((id) => !heldMajorStarSet.has(id));
  const coverageHoldReasons = Object.fromEntries([...(coveragePolicy?.palaceHolds ?? []), ...(coveragePolicy?.majorStarHolds ?? [])].map((item) => [item.id,item.reason]));
  const dimensions = Object.keys(dimensionPolarityRuleCounts).sort();
  const dimensionsWithoutCounterRules = dimensions.filter((id) => dimensionPolarityRuleCounts[id]!.counter === 0);
  const dimensionsWithoutSupportRules = dimensions.filter((id) => dimensionPolarityRuleCounts[id]!.support === 0);

  return Object.freeze({
    ruleCount: rulePack.rules.length,
    claimKeyCount: claimKeys.size,
    topicRuleCounts: Object.freeze({ ...topicRuleCounts }),
    sourceRuleCounts: Object.freeze({ ...sourceRuleCounts }),
    sourceLocatorRuleCounts: Object.freeze({ ...sourceLocatorRuleCounts }),
    polarityRuleCounts: Object.freeze({ ...polarityRuleCounts }),
    conditionRuleCounts: Object.freeze({ ...conditionRuleCounts }),
    referencedPalaceRuleCounts: Object.freeze({ ...referencedPalaceRuleCounts }),
    referencedStarRuleCounts: Object.freeze({ ...referencedStarRuleCounts }),
    unreferencedPalaceIds: Object.freeze([...unreferencedPalaceIds]),
    unreferencedMajorStarIds: Object.freeze([...unreferencedMajorStarIds]),
    heldPalaceIds: Object.freeze([...heldPalaceIds]),
    heldMajorStarIds: Object.freeze([...heldMajorStarIds]),
    actionableUnreferencedPalaceIds: Object.freeze([...actionableUnreferencedPalaceIds]),
    actionableUnreferencedMajorStarIds: Object.freeze([...actionableUnreferencedMajorStarIds]),
    coverageHoldReasons: Object.freeze({ ...coverageHoldReasons }),
    dimensionPolarityRuleCounts: Object.freeze(Object.fromEntries(
      Object.entries(dimensionPolarityRuleCounts).sort(([a],[b]) => a.localeCompare(b)).map(([key, value]) => [key, Object.freeze({ ...value })]),
    )),
    dimensionsWithoutCounterRules: Object.freeze(dimensionsWithoutCounterRules),
    dimensionsWithoutSupportRules: Object.freeze(dimensionsWithoutSupportRules),
    singleFeatureRuleCount,
    multiFeatureRuleCount,
    claimKeysWithoutTemplates: Object.freeze(claimKeysWithoutTemplates),
    templatesWithoutRules: Object.freeze(templatesWithoutRules),
    incompleteProvenanceRuleIds: Object.freeze(incompleteProvenanceRuleIds.sort()),
    disclosure: '這是開發期規則覆蓋與可追溯性 QA；未覆蓋項目代表研究／規則缺口，不是使用者命盤的準確率、信心值或預測機率。',
  });
}
